// absolute imports
import { Badge, CircularProgress, Fab, Tooltip, lighten } from '@mui/material';
import { green, red } from '@mui/material/colors';
import { darken } from '@mui/material/styles';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

// icon imports
import {
  Check as CheckIcon,
  PriorityHigh,
  Refresh as RefreshIcon,
  SyncProblem as SyncProblemIcon,
} from '@mui/icons-material';

// relative imports
import {
  isFeatureEnabled,
  isModuleEnabled,
} from '../../../data/app-config/configs/configUtilities';
import { useAccount } from '../../providers/AccountProvider';
import { useModalActions } from '../../providers/ModalActionsProvider';
import useAlternativeDisplay from '../../shared/custom-hooks/useAlternativeDisplay';
import usePrevious from '../../shared/custom-hooks/usePrevious';
import useUpdatePolling from '../../shared/custom-hooks/useUpdatePolling';

const useStyles = makeStyles()((theme, { isMobile }) => ({
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative',
  },
  buttonMain: {
    zIndex: 2,
    width: isMobile ? 28 : 26,
    height: isMobile ? 28 : 26,
    minHeight: isMobile ? 28 : 26,
    color: theme.whiteLabel.UpdateButton.color || theme.palette.common.white,
    backgroundColor:
      theme.whiteLabel.UpdateButton.backgroundColor ||
      'rgba(255, 255, 255, 0.2)',
    '&:hover': {
      backgroundColor: theme.whiteLabel.UpdateButton.backgroundColor
        ? darken(theme.whiteLabel.UpdateButton.backgroundColor, 0.3)
        : 'rgba(255, 255, 255, 0.3)',
    },
    marginLeft: theme.spacing(1),
  },
  buttonSuccess: {
    backgroundColor: green[500],
    '&:hover': {
      backgroundColor: green[700],
    },
    border: `2px solid ${
      theme.whiteLabel.UpdateButton.backgroundColor ||
      theme.palette.common.white
    }`,
  },
  buttonError: {
    backgroundColor: red[500],
    '&:hover': {
      backgroundColor: red[700],
    },
    border: `2px solid ${theme.palette.common.white}`,
  },
  buttonLoading: {
    backgroundColor: theme.whiteLabel.UpdateButton.backgroundColor
      ? lighten(theme.whiteLabel.UpdateButton.backgroundColor, 0.2)
      : 'rgba(255, 255, 255, 0.4)',
    '&:hover': {
      backgroundColor: theme.whiteLabel.UpdateButton.backgroundColor
        ? lighten(theme.whiteLabel.UpdateButton.backgroundColor, 0.3)
        : 'rgba(255, 255, 255, 0.4)',
    },
  },
  fabProgress: {
    color: green[500],
    position: 'absolute',
    top: isMobile ? 3.1 : -4,
    left: isMobile ? 4.2 : 4,
    zIndex: 1,
    opacity: 0.8,
  },
  statusBadge: {
    width: 14,
    height: 14,
    minWidth: 12,
    transform: 'scale(1) translate(60%, -30%)',
    boxShadow: theme.shadows[4],
    zIndex: 2,
  },
  warningStatus: {
    backgroundColor: theme.palette.warning.main,
  },
  errorStatus: {
    backgroundColor: theme.palette.error.main,
  },
  badgeIcon: {
    fontSize: 10,
    color: theme.palette.common.white,
  },
}));

const UpdateAccountDataButton = () => {
  const { isMobile } = useAlternativeDisplay();
  const { classes } = useStyles({ isMobile });
  const { currentAccount } = useAccount();
  const { showModal } = useModalActions();
  const { updating, taxUpdater, usage } = currentAccount;
  const { taxUpdateFailed, taxUpdateInProgress, taxUpdateNeedsRerun } =
    taxUpdater || {};
  const noSources =
    usage?.numberOfExchangeAccounts === 0 && usage?.numberOfWallets === 0;
  const [loading, setLoading] = useState(updating);
  const [completeStatus, setCompleteStatus] = useState();

  // set refs
  const updatingRef = useRef(updating);
  const accountIdRef = useRef(currentAccount.id);
  const timerRef = useRef(null);
  const previousTaxUpdateInProgress = usePrevious(taxUpdateInProgress);

  const buttonClassname = clsx({
    [classes.buttonMain]: true,
    [classes.buttonSuccess]: completeStatus === 'success' && !loading,
    [classes.buttonError]: completeStatus === 'error' && !loading,
    [classes.buttonLoading]: loading,
  });

  const badgeClassname = clsx({
    [classes.statusBadge]: true,
    [classes.errorStatus]: taxUpdateFailed,
    [classes.warningStatus]: taxUpdateNeedsRerun,
  });

  // handle modal opening
  const handleOpenClick = () => {
    showModal('TRIGGER_ACCOUNT_UPDATE', {
      updateType: isFeatureEnabled({
        moduleName: 'tax',
        featureName: 'triggerTaxUpdate',
      })
        ? 'both'
        : 'sources',
    });
  };

  const handleUpdateFinished = useCallback(() => {
    if (taxUpdateFailed) {
      setCompleteStatus('error');
    } else {
      setCompleteStatus('success');
      timerRef.current = window.setTimeout(() => {
        setCompleteStatus(null);
      }, 1500);
    }
  }, [taxUpdateFailed]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Track account updates using polling
  const updateCondition = updating || taxUpdateInProgress;

  const onUpdateStart = useCallback(() => {
    // When update starts, set loading and clear any previous status
    setLoading(true);
    setCompleteStatus(null);
  }, []);

  const onUpdateComplete = useCallback(() => {
    // When update completes, check if it failed
    if (taxUpdateFailed) {
      // Tax update failed - clear loading and show error
      setLoading(false);
      setCompleteStatus('error');
    } else {
      // Update completed successfully
      setLoading(false);
      handleUpdateFinished();
    }
  }, [taxUpdateFailed, handleUpdateFinished]);

  useUpdatePolling({
    preset: 'account',
    updateCondition,
    onUpdateStart,
    onUpdateComplete,
    refetchQueryList: ['GetAccount'],
  });

  // Handle account switching - reset state when account changes
  useEffect(() => {
    if (accountIdRef.current !== currentAccount.id) {
      accountIdRef.current = currentAccount.id;
      setCompleteStatus(null);
      setLoading(updating);
    }
  }, [currentAccount.id, updating]);

  // Sync loading state with updating when it changes (for immediate UI feedback)
  useEffect(() => {
    if (updating !== updatingRef.current) {
      updatingRef.current = updating;
      setLoading(updating);
      if (updating) {
        setCompleteStatus(null);
      }
    }
  }, [updating]);

  // Handle tax update failure when taxUpdateInProgress transitions from true to false
  useEffect(() => {
    // Check if taxUpdateInProgress transitioned from true to false
    const taxUpdateJustFinished =
      previousTaxUpdateInProgress === true && taxUpdateInProgress === false;

    if (taxUpdateJustFinished && taxUpdateFailed) {
      // Tax update finished but failed - clear loading and show error
      setLoading(false);
      setCompleteStatus('error');
    } else if (taxUpdateJustFinished && !taxUpdateFailed) {
      // Tax update finished successfully - clear loading if not already updating
      if (!updating) {
        setLoading(false);
        handleUpdateFinished();
      }
    }
  }, [
    taxUpdateInProgress,
    previousTaxUpdateInProgress,
    taxUpdateFailed,
    updating,
    handleUpdateFinished,
  ]);

  return (
    <span>
      <div className={classes.wrapper}>
        <Tooltip
          title="Account Refresh Options"
          enterDelay={800}
          leaveDelay={200}
          placement="right"
          enterTouchDelay={10}
        >
          <Badge
            color="secondary"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            badgeContent={<PriorityHigh className={classes.badgeIcon} />}
            classes={{
              anchorOriginTopRightRectangular: badgeClassname,
            }}
            invisible={
              !isModuleEnabled({ moduleName: 'tax' }) ||
              completeStatus === 'success' ||
              (loading && !taxUpdateFailed) ||
              (updating && !taxUpdateFailed) ||
              (!taxUpdateFailed && !taxUpdateNeedsRerun) ||
              noSources
            }
          >
            <Fab
              aria-label="update all data sources"
              className={buttonClassname}
              onClick={handleOpenClick}
              id="update-all-sources-icon-button"
            >
              {(!completeStatus || loading) && <RefreshIcon fontSize="small" />}
              {completeStatus === 'success' && !loading && (
                <CheckIcon fontSize="small" />
              )}
              {completeStatus === 'error' && !loading && (
                <SyncProblemIcon fontSize="small" />
              )}
            </Fab>
          </Badge>
        </Tooltip>
        {loading && (
          <CircularProgress
            size={isMobile ? 36 : 34}
            thickness={5}
            className={classes.fabProgress}
          />
        )}
      </div>
    </span>
  );
};

export default UpdateAccountDataButton;
